import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLocation as useLocationContext } from '../../contexts/LocationContext';
import { useBusinessTypeContext } from '../../contexts/BusinessTypeContext';
import { useAuth } from '../../contexts/AuthContext';
import apiClient, { getErrorMessage } from '../../utils/api';
import { SalaryGeneration as SalaryGenerationType, SalaryStatus, SalaryPeriodType, Employee } from '../../types/api';
import { PlusIcon, CheckCircleIcon, XMarkIcon, CurrencyDollarIcon, ArrowLeftIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

interface SalaryGenerateFormData {
  employee_id: string;
  period_type: SalaryPeriodType;
  period_start_date: string;
  period_end_date: string;
  override_daily_wage_rate?: number;
  business_type: 'fish' | 'goat';
}

const salarySchema = yup.object({
  employee_id: yup.string().required('Employee is required'),
  period_type: yup.string().oneOf(['monthly', 'half_monthly', 'weekly']).required('Period type is required'),
  period_start_date: yup.string().required('Start date is required'),
  period_end_date: yup.string().required('End date is required'),
  override_daily_wage_rate: yup.number()
    .transform((value, originalValue) => {
      // Convert empty string or NaN to undefined
      if (originalValue === '' || isNaN(value) || value === null) {
        return undefined;
      }
      return value;
    })
    .positive('Rate must be positive')
    .nullable()
    .optional(),
  business_type: yup.string().oneOf(['fish', 'goat']).required('Business type is required'),
});

const SalaryGenerationPage: React.FC = () => {
  const { selectedLocation } = useLocationContext();
  const { businessType } = useBusinessTypeContext();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [salaries, setSalaries] = useState<SalaryGenerationType[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateForm, setShowGenerateForm] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState<SalaryGenerationType | null>(null);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | SalaryStatus>('all');
  const [generating, setGenerating] = useState(false);
  const periodTypeManuallyChanged = React.useRef(false);

  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';
  const isAdmin = user?.role === 'admin';

  const form = useForm<SalaryGenerateFormData>({
    resolver: yupResolver(salarySchema) as any,
    defaultValues: {
      employee_id: '',
      period_type: 'monthly',
      period_start_date: '',
      period_end_date: '',
      override_daily_wage_rate: undefined,
      business_type: businessType || 'fish',
    },
  });

  const selectedEmployeeId = form.watch('employee_id');
  const selectedEmployee = selectedEmployeeId ? employees.find(e => e.id === selectedEmployeeId) : null;
  const employeeType = selectedEmployee?.employee_type || null;
  const periodType = form.watch('period_type');

  useEffect(() => {
    if (selectedLocation) {
      fetchSalaries();
      fetchEmployees();
    }
  }, [selectedLocation, statusFilter]); // Removed businessType dependency to show all business types

  // Calculate dates helper function
  const calculatePeriodDates = useCallback((periodTypeValue: SalaryPeriodType) => {
    const today = new Date();
    // Use local date to avoid timezone issues
    const year = today.getFullYear();
    const month = today.getMonth(); // 0-indexed (0 = January, 11 = December)
    let startDate: Date;
    let endDate: Date;

    if (periodTypeValue === 'monthly') {
      // Last month: 1st to last day of previous month
      const lastMonth = month === 0 ? 11 : month - 1; // If current month is January (0), last month is December (11)
      const lastMonthYear = month === 0 ? year - 1 : year; // If current month is January, last month is in previous year
      startDate = new Date(lastMonthYear, lastMonth, 1);
      endDate = new Date(lastMonthYear, lastMonth + 1, 0); // Day 0 of next month = last day of current month
    } else if (periodTypeValue === 'half_monthly') {
      // Current half-month period: 1-15 or 16-last day (both inclusive)
      const day = today.getDate();
      if (day <= 15) {
        startDate = new Date(year, month, 1);
        endDate = new Date(year, month, 15);
      } else {
        startDate = new Date(year, month, 16);
        endDate = new Date(year, month + 1, 0); // Last day of current month
      }
    } else { // weekly - Friday to Thursday (both inclusive)
      // Find most recent Friday (Friday = 5 in JavaScript Date.getDay())
      const dayOfWeek = today.getDay(); // 0=Sunday, 1=Monday, ..., 5=Friday, 6=Saturday
      let daysSinceFriday;
      if (dayOfWeek >= 5) {
        // Friday (5), Saturday (6)
        daysSinceFriday = dayOfWeek - 5;
      } else {
        // Sunday (0), Monday (1), Tuesday (2), Wednesday (3), Thursday (4)
        daysSinceFriday = dayOfWeek + 2; // Sunday (0) + 2 = 2 days back to Friday
      }
      // If today is not Friday, we want the previous Friday
      if (daysSinceFriday === 0 && dayOfWeek !== 5) {
        daysSinceFriday = 7;
      }
      const friday = new Date(today);
      friday.setDate(today.getDate() - daysSinceFriday);
      startDate = new Date(friday);
      endDate = new Date(friday);
      endDate.setDate(friday.getDate() + 6); // Thursday (6 days after Friday)
    }

    // Format dates as YYYY-MM-DD in local timezone to avoid timezone conversion issues
    const formatDate = (date: Date): string => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    form.setValue('period_start_date', formatDate(startDate));
    form.setValue('period_end_date', formatDate(endDate));
  }, [form]);

  // Auto-select business type when employee is selected or when form opens
  useEffect(() => {
    if (selectedEmployee && selectedEmployee.business_type) {
      form.setValue('business_type', selectedEmployee.business_type);
      // Reset period type manual change flag when employee changes
      periodTypeManuallyChanged.current = false;
    } else if (showGenerateForm && !form.watch('business_type')) {
      // Set default business type when form opens if not set
      form.setValue('business_type', businessType || 'fish');
    }
  }, [selectedEmployee, showGenerateForm, businessType, form]);

  // Auto-select period type based on employee type (only if not manually changed)
  useEffect(() => {
    if (employeeType && !periodTypeManuallyChanged.current && showGenerateForm) {
      let defaultPeriodType: SalaryPeriodType = 'monthly';
      
      if (employeeType === 'manager') {
        defaultPeriodType = 'monthly';
      } else if (employeeType === 'regular_farm_labour') {
        defaultPeriodType = 'half_monthly';
      } else if (employeeType === 'daily_wage_labour') {
        defaultPeriodType = 'weekly';
      }
      
      form.setValue('period_type', defaultPeriodType);
      // Calculate dates immediately when period type is set
      calculatePeriodDates(defaultPeriodType);
    }
  }, [employeeType, showGenerateForm, form, calculatePeriodDates]);

  // Auto-set period dates based on period type
  useEffect(() => {
    if (periodType && showGenerateForm) {
      calculatePeriodDates(periodType);
    }
  }, [periodType, showGenerateForm, calculatePeriodDates]);

  const fetchEmployees = async () => {
    if (!selectedLocation) return;
    try {
      // Fetch all active employees regardless of business type
      const response = await apiClient.get<Employee[]>(`/employees?location_id=${selectedLocation.id}&is_active=true`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchSalaries = async () => {
    if (!selectedLocation) return;
    setLoading(true);
    try {
      // Fetch all salaries regardless of business type
      const params = new URLSearchParams({
        location_id: selectedLocation.id,
        ...(statusFilter !== 'all' && { status: statusFilter }),
      });
      const response = await apiClient.get<SalaryGenerationType[]>(`/salary-generations?${params.toString()}`);
      setSalaries(response.data);
    } catch (error) {
      console.error('Error fetching salaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (data: SalaryGenerateFormData) => {
    if (!selectedLocation) {
      setFormError('Please select a location');
      return;
    }

    setFormError('');
    
    // Check for duplicate salary generation on frontend
    const existingSalary = salaries.find(s => 
      s.employee_id === data.employee_id &&
      s.period_start_date === data.period_start_date &&
      s.period_end_date === data.period_end_date &&
      (s.status === 'draft' || s.status === 'confirmed')
    );
    
    if (existingSalary) {
      setFormError('Salary already generated for this employee and date range. Please check the existing salary record.');
      return;
    }

    setGenerating(true);

    try {
      await apiClient.post('/salary-generations/generate', {
        employee_id: data.employee_id,
        location_id: selectedLocation.id,
        business_type: data.business_type,
        period_type: data.period_type,
        period_start_date: data.period_start_date,
        period_end_date: data.period_end_date,
        override_daily_wage_rate: data.override_daily_wage_rate && !isNaN(data.override_daily_wage_rate) ? data.override_daily_wage_rate : undefined,
      });

      setShowGenerateForm(false);
      periodTypeManuallyChanged.current = false;
      form.reset({
        employee_id: '',
        period_type: 'monthly',
        period_start_date: '',
        period_end_date: '',
        override_daily_wage_rate: undefined,
        business_type: businessType || 'fish',
      });
      fetchSalaries();
    } catch (error: any) {
      const errorMessage = getErrorMessage(error, 'Failed to generate salary');
      setFormError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleConfirm = async (salaryId: string) => {
    if (!window.confirm('Are you sure you want to confirm this salary? This will create a payment request.')) return;
    
    try {
      setSubmitting(true);
      await apiClient.post(`/salary-generations/${salaryId}/confirm`);
      setSelectedSalary(null);
      fetchSalaries();
      alert('Salary confirmed and payment request created successfully!');
    } catch (error: any) {
      alert(getErrorMessage(error, 'Failed to confirm salary'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRate = async (salaryId: string, overrideRate: number) => {
    try {
      await apiClient.put(`/salary-generations/${salaryId}`, {
        override_daily_wage_rate: overrideRate,
      });
      fetchSalaries();
      if (selectedSalary?.id === salaryId) {
        const updated = await apiClient.get<SalaryGenerationType>(`/salary-generations/${salaryId}`);
        setSelectedSalary(updated.data);
      }
    } catch (error: any) {
      alert(getErrorMessage(error, 'Failed to update rate'));
    }
  };

  const handleDelete = async (salaryId: string) => {
    if (!window.confirm('Are you sure you want to delete this salary record?')) return;
    try {
      await apiClient.delete(`/salary-generations/${salaryId}`);
      fetchSalaries();
      if (selectedSalary?.id === salaryId) {
        setSelectedSalary(null);
      }
      alert('Salary record deleted successfully');
    } catch (error: any) {
      alert(getErrorMessage(error, 'Failed to delete salary'));
    }
  };

  const handleCloseForm = () => {
    setShowGenerateForm(false);
    setFormError('');
    periodTypeManuallyChanged.current = false;
    form.reset({
      employee_id: '',
      period_type: 'monthly',
      period_start_date: '',
      period_end_date: '',
      override_daily_wage_rate: undefined,
      business_type: businessType || 'fish',
    });
  };

  const getStatusColor = (status: SalaryStatus) => {
    switch (status) {
      case 'confirmed':
        return 'bg-blue-100 text-blue-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!selectedLocation) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">Please select a location to view salaries.</p>
        </div>
      </div>
    );
  }

  const filteredSalaries = statusFilter === 'all' ? salaries : salaries.filter(s => s.status === statusFilter);

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <button
          onClick={() => navigate('/payroll')}
          className="mb-4 flex items-center text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          <span>Back to Payroll</span>
        </button>
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Salary Generation</h1>
            <p className="text-gray-600 mt-1">Generate and manage employee salaries</p>
          </div>
          {isManagerOrAdmin && (
            <button
              onClick={() => {
                periodTypeManuallyChanged.current = false;
                form.reset({
                  employee_id: '',
                  period_type: 'monthly',
                  period_start_date: '',
                  period_end_date: '',
                  override_daily_wage_rate: undefined,
                  business_type: businessType || 'fish',
                });
                setShowGenerateForm(true);
              }}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <PlusIcon className="h-5 w-5" />
              <span>Generate Salary</span>
            </button>
          )}
        </div>
      </div>

      {/* Filter */}
      <div className="mb-4 flex space-x-2">
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            statusFilter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setStatusFilter('draft')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            statusFilter === 'draft' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Draft
        </button>
        <button
          onClick={() => setStatusFilter('confirmed')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            statusFilter === 'confirmed' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Confirmed
        </button>
        <button
          onClick={() => setStatusFilter('paid')}
          className={`px-4 py-2 rounded-lg text-sm font-medium ${
            statusFilter === 'paid' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Paid
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <>
          {/* Mobile Card View */}
          <div className="block md:hidden space-y-2">
            {filteredSalaries.map((salary) => {
              const employee = employees.find(e => e.id === salary.employee_id);
              const isDailyWageLabour = employee?.employee_type === 'daily_wage_labour';
              const canDelete = isAdmin || (user?.role === 'manager' && salary.status === 'draft');
              
              return (
                <div key={salary.id} className="bg-white rounded-lg shadow p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0 pr-2">
                      <h3 className="text-base font-semibold text-gray-900 mb-0.5 flex items-baseline gap-2">
                        <span className="truncate">{salary.employee_name || 'N/A'}</span>
                        <span className="flex-shrink-0 font-bold text-green-600">₹{salary.net_amount.toFixed(2)}</span>
                      </h3>
                      {employee?.employee_code && (
                        <p className="text-xs text-gray-500 mb-1">{employee.employee_code}</p>
                      )}
                      <p className="text-xs text-gray-500">
                        {new Date(salary.period_start_date).toLocaleDateString('en-IN', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })} - {new Date(salary.period_end_date).toLocaleDateString('en-IN', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1.5 flex-shrink-0">
                      <span className={`px-1.5 py-0.5 text-xs rounded-full ${getStatusColor(salary.status)}`}>
                        {salary.status.charAt(0).toUpperCase() + salary.status.slice(1)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-1 text-xs mb-2">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Period Type:</span>
                      <span className="text-gray-900 capitalize">
                        {salary.period_type.replace('_', ' ')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Business Type:</span>
                      <span className="text-gray-900 capitalize">{salary.business_type}</span>
                    </div>
                    {salary.location_name && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Location:</span>
                        <span className="text-gray-900 truncate ml-2">{salary.location_name}</span>
                      </div>
                    )}
                    {employee?.monthly_salary && !isDailyWageLabour && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Monthly Salary:</span>
                        <span className="text-gray-900 font-semibold">
                          ₹{employee.monthly_salary.toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Calculated Amount:</span>
                      <span className="text-gray-900 font-medium">
                        ₹{salary.calculated_amount.toFixed(2)}
                      </span>
                    </div>
                    {salary.advance_deductions > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span className="text-gray-500">Advance Deductions:</span>
                        <span className="font-medium">-₹{salary.advance_deductions.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total Days:</span>
                      <span className="text-gray-900">{salary.total_days}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Present:</span>
                      <span className="text-green-600 font-medium">{salary.present_days}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Absent:</span>
                      <span className="text-red-600 font-medium">{salary.absent_days}</span>
                    </div>
                    {salary.leave_days > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Leave:</span>
                        <span className="text-blue-600 font-medium">{salary.leave_days}</span>
                      </div>
                    )}
                    {salary.half_days > 0 && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Half Days:</span>
                        <span className="text-gray-900">{salary.half_days}</span>
                      </div>
                    )}
                    {salary.daily_wage_rate_used && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Daily Wage Rate:</span>
                        <span className="text-gray-900">₹{salary.daily_wage_rate_used}/day</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Created:</span>
                      <span className="text-gray-900">
                        {new Date(salary.created_at).toLocaleDateString('en-IN', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>
                  
                  {selectedSalary?.id === salary.id && (
                    <div className="mt-3 pt-3 border-t space-y-3">
                      {salary.status === 'draft' && isManagerOrAdmin && (
                        <>
                          {isDailyWageLabour && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Override Daily Wage Rate (₹)</label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                defaultValue={salary.override_daily_wage_rate || salary.daily_wage_rate_used || ''}
                                onBlur={(e) => {
                                  const rate = parseFloat(e.target.value);
                                  if (rate && rate !== (salary.override_daily_wage_rate || salary.daily_wage_rate_used)) {
                                    handleUpdateRate(salary.id, rate);
                                  }
                                }}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="Override rate"
                              />
                            </div>
                          )}
                          <button
                            onClick={() => handleConfirm(salary.id)}
                            disabled={submitting}
                            className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center justify-center space-x-2 text-sm font-medium"
                          >
                            <CheckCircleIcon className="h-4 w-4" />
                            <span>Confirm & Create Payment Request</span>
                          </button>
                        </>
                      )}
                      {salary.payment_request_id && (
                        <div className="text-xs text-gray-500">
                          Payment Request ID: {salary.payment_request_id}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {isManagerOrAdmin && (
                    <div className="flex space-x-1.5 pt-2 border-t">
                      <button
                        onClick={() => setSelectedSalary(selectedSalary?.id === salary.id ? null : salary)}
                        className="flex-1 text-blue-600 hover:text-blue-900 px-2 py-1.5 border border-blue-300 rounded hover:bg-blue-50 flex items-center justify-center space-x-1"
                      >
                        <span className="text-xs">{selectedSalary?.id === salary.id ? 'Hide' : 'Details'}</span>
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(salary.id)}
                          className="text-red-600 hover:text-red-900 p-1.5 border border-red-300 rounded hover:bg-red-50"
                          title="Delete"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            
            {filteredSalaries.length === 0 && (
              <div className="bg-white rounded-lg shadow p-4 text-center text-gray-500">
                <p className="text-sm">No salary records found. {isManagerOrAdmin && 'Click "Generate Salary" to create one.'}</p>
              </div>
            )}
          </div>

          {/* Desktop Table View */}
          <div className="hidden md:block space-y-4">
            {filteredSalaries.map((salary) => {
              const employee = employees.find(e => e.id === salary.employee_id);
              const isDailyWageLabour = employee?.employee_type === 'daily_wage_labour';
              const canDelete = isAdmin || (user?.role === 'manager' && salary.status === 'draft');
              
              return (
                <div key={salary.id} className="bg-white rounded-lg shadow p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 flex items-baseline gap-3">
                        <span className="truncate">{salary.employee_name || 'N/A'}</span>
                        <span className="flex-shrink-0 font-bold text-green-600">₹{salary.net_amount.toFixed(2)}</span>
                      </h3>
                      {employee?.employee_code && (
                        <p className="text-sm text-gray-500">Code: {employee.employee_code}</p>
                      )}
                      <p className="text-sm text-gray-600">
                        {new Date(salary.period_start_date).toLocaleDateString()} - {new Date(salary.period_end_date).toLocaleDateString()}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-1">
                        <span className="text-xs text-gray-500 capitalize">
                          {salary.period_type.replace('_', ' ')} Period
                        </span>
                        <span className="text-xs text-gray-500">•</span>
                        <span className="text-xs text-gray-500 capitalize">{salary.business_type}</span>
                        {salary.location_name && (
                          <>
                            <span className="text-xs text-gray-500">•</span>
                            <span className="text-xs text-gray-500">{salary.location_name}</span>
                          </>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4 text-sm">
                        {employee?.monthly_salary && !isDailyWageLabour && (
                          <div>
                            <span className="text-gray-500">Monthly Salary: </span>
                            <span className="font-semibold">₹{employee.monthly_salary.toFixed(2)}</span>
                          </div>
                        )}
                        <div>
                          <span className="text-gray-500">Calculated: </span>
                          <span className="font-medium">₹{salary.calculated_amount.toFixed(2)}</span>
                        </div>
                        {salary.advance_deductions > 0 && (
                          <div>
                            <span className="text-gray-500">Deductions: </span>
                            <span className="font-medium text-red-600">-₹{salary.advance_deductions.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 flex flex-wrap gap-4 text-xs text-gray-500">
                        <span>Days: {salary.total_days}</span>
                        <span className="text-green-600">Present: {salary.present_days}</span>
                        <span className="text-red-600">Absent: {salary.absent_days}</span>
                        {salary.leave_days > 0 && <span className="text-blue-600">Leave: {salary.leave_days}</span>}
                        {salary.half_days > 0 && <span>Half Days: {salary.half_days}</span>}
                        {salary.daily_wage_rate_used && (
                          <span>Rate: ₹{salary.daily_wage_rate_used}/day</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-1">
                        Created: {new Date(salary.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className={`px-3 py-1 text-sm rounded-full ${getStatusColor(salary.status)}`}>
                        {salary.status.charAt(0).toUpperCase() + salary.status.slice(1)}
                      </span>
                      <button
                        onClick={() => setSelectedSalary(selectedSalary?.id === salary.id ? null : salary)}
                        className="text-blue-600 hover:text-blue-900 text-sm"
                      >
                        {selectedSalary?.id === salary.id ? 'Hide Details' : 'View Details'}
                      </button>
                      {canDelete && (
                        <button
                          onClick={() => handleDelete(salary.id)}
                          className="text-red-600 hover:text-red-900 transition-colors"
                          title="Delete"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>

                {selectedSalary?.id === salary.id && (
                  <div className="mt-4 pt-4 border-t space-y-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Total Days</p>
                        <p className="text-lg font-semibold">{salary.total_days}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Present</p>
                        <p className="text-lg font-semibold text-green-600">{salary.present_days}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Absent</p>
                        <p className="text-lg font-semibold text-red-600">{salary.absent_days}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Leave</p>
                        <p className="text-lg font-semibold text-blue-600">{salary.leave_days}</p>
                      </div>
                    </div>

                    {salary.half_days > 0 && (
                      <div>
                        <p className="text-xs text-gray-500">Half Days</p>
                        <p className="text-sm font-semibold">{salary.half_days}</p>
                      </div>
                    )}

                    {salary.daily_wage_rate_used && (
                      <div>
                        <p className="text-xs text-gray-500">Daily Wage Rate Used</p>
                        <p className="text-sm font-semibold">₹{salary.daily_wage_rate_used}/day</p>
                      </div>
                    )}

                    <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                      {employee?.monthly_salary && !isDailyWageLabour && (
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Monthly Salary:</span>
                          <span className="text-sm font-semibold">₹{employee.monthly_salary.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Calculated Amount:</span>
                        <span className="text-sm font-semibold">₹{salary.calculated_amount.toFixed(2)}</span>
                      </div>
                      {salary.advance_deductions > 0 && (
                        <div className="flex justify-between text-red-600">
                          <span className="text-sm">Advance Deductions:</span>
                          <span className="text-sm font-semibold">-₹{salary.advance_deductions.toFixed(2)}</span>
                        </div>
                      )}
                      <div className="flex justify-between pt-2 border-t border-gray-200">
                        <span className="text-base font-semibold text-gray-900">Net Amount:</span>
                        <span className="text-lg font-bold text-green-600">₹{salary.net_amount.toFixed(2)}</span>
                      </div>
                    </div>

                    {salary.status === 'draft' && isManagerOrAdmin && (
                      <div className="flex space-x-3 pt-2">
                        {isDailyWageLabour && (
                          <div className="flex-1">
                            <label className="block text-xs text-gray-500 mb-1">Override Daily Wage Rate (₹)</label>
                            <div className="flex space-x-2">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                defaultValue={salary.override_daily_wage_rate || salary.daily_wage_rate_used || ''}
                                onBlur={(e) => {
                                  const rate = parseFloat(e.target.value);
                                  if (rate && rate !== (salary.override_daily_wage_rate || salary.daily_wage_rate_used)) {
                                    handleUpdateRate(salary.id, rate);
                                  }
                                }}
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                placeholder="Override rate"
                              />
                            </div>
                          </div>
                        )}
                        <button
                          onClick={() => handleConfirm(salary.id)}
                          disabled={submitting}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                        >
                          <CheckCircleIcon className="h-5 w-5" />
                          <span>Confirm & Create Payment Request</span>
                        </button>
                      </div>
                    )}

                    {salary.payment_request_id && (
                      <div className="text-xs text-gray-500">
                        Payment Request ID: {salary.payment_request_id}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

            {filteredSalaries.length === 0 && (
              <div className="text-center py-12 text-gray-500 bg-white rounded-lg shadow">
                <p>No salary records found. {isManagerOrAdmin && 'Click "Generate Salary" to create one.'}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Generate Salary Form Modal */}
      {showGenerateForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">Generate Salary</h2>
              <button
                onClick={handleCloseForm}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {formError && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-red-800 text-sm">{formError}</p>
              </div>
            )}

            <form onSubmit={form.handleSubmit(handleGenerate)} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Employee */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employee <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('employee_id')}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.employee_id ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="">Select employee</option>
                    {employees.map((emp) => (
                      <option key={emp.id} value={emp.id}>
                        {emp.name} ({emp.employee_code}) - {emp.employee_type.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                  {form.formState.errors.employee_id && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.employee_id.message}</p>
                  )}
                </div>

                {/* Business Type - Auto-selected, read-only */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Business Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('business_type')}
                    disabled
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-100 cursor-not-allowed ${
                      form.formState.errors.business_type ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="fish">Fish</option>
                    <option value="goat">Goat</option>
                  </select>
                  {form.formState.errors.business_type && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.business_type.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Auto-selected based on employee</p>
                </div>

                {/* Period Type - Auto-selected but editable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Period Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    {...form.register('period_type')}
                    onChange={(e) => {
                      periodTypeManuallyChanged.current = true;
                      form.setValue('period_type', e.target.value as SalaryPeriodType);
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.period_type ? 'border-red-500' : 'border-gray-300'
                    }`}
                  >
                    <option value="monthly">Monthly</option>
                    <option value="half_monthly">Half Monthly (1-15 / 16-last day)</option>
                    <option value="weekly">Weekly (Fri-Thu)</option>
                  </select>
                  {form.formState.errors.period_type && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.period_type.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    {employeeType === 'manager' && 'Auto-selected: Monthly (can be changed)'}
                    {employeeType === 'regular_farm_labour' && 'Auto-selected: Half Monthly (can be changed)'}
                    {employeeType === 'daily_wage_labour' && 'Auto-selected: Weekly (can be changed)'}
                    {!employeeType && 'Select employee to auto-select period type'}
                  </p>
                </div>

                {/* Start Date - Auto-selected but editable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...form.register('period_start_date')}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.period_start_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {form.formState.errors.period_start_date && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.period_start_date.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Auto-selected based on period type (can be changed)</p>
                </div>

                {/* End Date - Auto-selected but editable */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    {...form.register('period_end_date')}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      form.formState.errors.period_end_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {form.formState.errors.period_end_date && (
                    <p className="mt-1 text-sm text-red-600">{form.formState.errors.period_end_date.message}</p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">Auto-selected based on period type (can be changed)</p>
                </div>

                {/* Override Daily Wage Rate (only for daily wage labour) */}
                {employeeType === 'daily_wage_labour' && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Override Daily Wage Rate (₹)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...form.register('override_daily_wage_rate', {
                        valueAsNumber: true,
                        setValueAs: (value) => {
                          // Convert empty string or NaN to undefined
                          if (value === '' || isNaN(value) || value === null) {
                            return undefined;
                          }
                          return Number(value);
                        }
                      })}
                      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                        form.formState.errors.override_daily_wage_rate ? 'border-red-500' : 'border-gray-300'
                      }`}
                      placeholder="Leave empty to use default/attendance rates"
                    />
                    {form.formState.errors.override_daily_wage_rate && (
                      <p className="mt-1 text-sm text-red-600">{form.formState.errors.override_daily_wage_rate.message}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">Optional: Override daily wage rate for this salary period</p>
                  </div>
                )}
              </div>

              {/* Submit Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t mt-6">
                <button
                  type="button"
                  onClick={handleCloseForm}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={generating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={generating}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {generating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      <span>Generating...</span>
                    </>
                  ) : (
                    <>
                      <CurrencyDollarIcon className="h-5 w-5" />
                      <span>Generate Salary</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalaryGenerationPage;
